import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, X, Trash2, Shield, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: "text-amber-400 border-amber-400/40",
  approved: "text-emerald-400 border-emerald-400/40",
  rejected: "text-rose-400 border-rose-400/40",
};

export default function AdminReviews() {
  const [token, setToken] = useState(localStorage.getItem("mrs_admin_token") || "");
  const [tokenInput, setTokenInput] = useState("");
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);

  const load = async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reviews`, {
        headers: { "X-Admin-Token": tok || token },
      });
      setReviews(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin token");
        setToken("");
        localStorage.removeItem("mrs_admin_token");
      } else {
        toast.error("Could not load reviews");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line
  }, [token]);

  const login = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    localStorage.setItem("mrs_admin_token", tokenInput.trim());
    setToken(tokenInput.trim());
  };

  const logout = () => {
    localStorage.removeItem("mrs_admin_token");
    setToken("");
    setReviews([]);
  };

  const act = async (id, action) => {
    try {
      if (action === "delete") {
        await axios.delete(`${API}/admin/reviews/${id}`, {
          headers: { "X-Admin-Token": token },
        });
      } else {
        await axios.post(
          `${API}/admin/reviews/${id}/${action}`,
          {},
          { headers: { "X-Admin-Token": token } }
        );
      }
      toast.success(`Review ${action}d`);
      load(token);
    } catch {
      toast.error(`Failed to ${action}`);
    }
  };

  const filtered = filter === "all" ? reviews : reviews.filter((r) => r.status === filter);
  const counts = {
    all: reviews.length,
    pending: reviews.filter((r) => r.status === "pending").length,
    approved: reviews.filter((r) => r.status === "approved").length,
    rejected: reviews.filter((r) => r.status === "rejected").length,
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center px-6" data-testid="admin-login">
        <form onSubmit={login} className="tracing-beam p-1 w-full max-w-md">
          <div className="bg-[#0B1120]/90 p-8">
            <Shield size={28} className="text-orange-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl tracking-tight text-center chrome-text">
              Admin Access
            </h1>
            <p className="font-body text-slate-400 text-sm text-center mt-2">
              Enter the admin token to manage reviews.
            </p>

            <input
              type="password"
              placeholder="Admin Token"
              value={tokenInput}
              onChange={(e) => setTokenInput(e.target.value)}
              data-testid="admin-token-input"
              className="w-full mt-6 bg-slate-900/60 border border-slate-700 px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-orange-500/60"
            />
            <button
              type="submit"
              data-testid="admin-login-btn"
              className="btn-forge w-full mt-4"
            >
              Sign In
            </button>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-2 font-tech text-[11px] text-slate-500 hover:text-orange-400"
            >
              <ArrowLeft size={12} /> Back to site
            </Link>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120] py-10 px-6" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 font-tech text-[11px] text-slate-400 hover:text-orange-400 mb-3"
            >
              <ArrowLeft size={12} /> Site
            </Link>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight chrome-text">
              Reviews Management
            </h1>
          </div>
          <button
            onClick={logout}
            className="btn-chrome text-[11px] py-2 px-4 self-start"
            data-testid="admin-logout-btn"
          >
            Sign Out
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`font-tech text-[11px] px-4 py-2 border transition-colors ${
                filter === f
                  ? "border-orange-500/70 text-orange-300 bg-orange-500/10"
                  : "border-slate-700 text-slate-400 hover:border-slate-500"
              }`}
            >
              {f.toUpperCase()} ({counts[f] || 0})
            </button>
          ))}
        </div>

        {loading && <div className="text-slate-400 font-tech text-xs">Loading...</div>}

        <div className="space-y-3">
          {filtered.length === 0 && !loading && (
            <div className="forge-card p-8 text-center text-slate-400 font-body text-sm">
              No reviews here.
            </div>
          )}
          {filtered.map((r) => (
            <div key={r.id} className="forge-card p-5" data-testid={`admin-review-${r.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-display text-lg tracking-tight text-slate-100">
                    {r.title}
                  </div>
                  <div className="font-tech text-[10px] text-slate-500 mt-0.5">
                    {r.name} {r.company ? `· ${r.company}` : ""}
                  </div>
                </div>
                <div className={`font-tech text-[10px] px-2 py-1 border ${STATUS_COLORS[r.status]}`}>
                  {r.status.toUpperCase()}
                </div>
              </div>

              <div className="flex items-center gap-1 mb-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={14}
                    className={i < r.rating ? "fill-amber-400 text-amber-400" : "text-slate-700"}
                  />
                ))}
              </div>

              <p className="font-body text-slate-300 text-sm leading-relaxed">{r.content}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {r.status !== "approved" && (
                  <button
                    onClick={() => act(r.id, "approve")}
                    data-testid={`admin-approve-${r.id}`}
                    className="font-tech text-[10px] px-3 py-1.5 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 inline-flex items-center gap-1.5"
                  >
                    <Check size={12} /> Approve
                  </button>
                )}
                {r.status !== "rejected" && (
                  <button
                    onClick={() => act(r.id, "reject")}
                    data-testid={`admin-reject-${r.id}`}
                    className="font-tech text-[10px] px-3 py-1.5 border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 inline-flex items-center gap-1.5"
                  >
                    <X size={12} /> Reject
                  </button>
                )}
                <button
                  onClick={() => act(r.id, "delete")}
                  data-testid={`admin-delete-${r.id}`}
                  className="font-tech text-[10px] px-3 py-1.5 border border-slate-600 text-slate-400 hover:bg-slate-800 inline-flex items-center gap-1.5"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
